// src/receptionist/components/BookingDialogs.tsx
//
// Everything the console can still do about a booking, in one dialog: send one
// of the approved templates, or decline the request. They belong together
// because they answer the same question — what happens next for this customer
// — and splitting them across two buttons made the drawer footer a row of
// verbs with no shared frame.
//
// The shape is the candidate drawer's Trigger Step dialog
// (containers/dialog/dialog-trigger-step.tsx): say what will happen, let the
// reader choose, show them the message, and only then send.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2Icon, MessageCircleIcon, XCircleIcon, ZapIcon } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@component/dialog";
import { Button } from "@component/button";
import { Textarea } from "@component/textarea";
import { Label } from "@component/label";
import { getFlow, sendBookingMessage, declineBooking, type WhatsappTemplateKey } from "@service/receptionist";
import type { FollowUp } from "../flow";
import { isReplyWindowOpen, slotPlaceholders, slotWhen, type Booking } from "../bookings";
import { WaPreview } from "./ui";
import { cn } from "@oura/lib/cn";

/**
 * The follow-up list and the WhatsApp template pack are the same seven things
 * under two names: the flow carries the wording and the approval status, the
 * send takes Meta's key. Joined on the label they share; a follow-up that maps
 * to no key is not offered, because there is nothing to send it as.
 */
const KEY_BY_LABEL: Record<string, WhatsappTemplateKey> = {
    "Booking confirmed": "booking_confirmed",
    "Day-before reminder": "reminder_24h",
    "Same-day reminder": "reminder_same_day",
    "Nudge after silence": "stall_nudge",
    "Post-visit rebooking": "post_visit_rebook",
    "Missed appointment": "no_show_recovery",
    "Morning digest (to your team)": "morning_digest",
};

/** {service} and friends, filled in from the booking so the preview is real. */
function fill(message: string, booking: Booking): string {
    // As the backend fills them (bookings.slot_parts), so the preview is what goes out.
    const { date, time } = slotPlaceholders(booking);
    return message
        .replace(/\{service\}/g, booking.service)
        .replace(/\{date\}/g, date || booking.slot)
        .replace(/\{time\}/g, time || booking.slot)
        .replace(/\{name\}/g, booking.customer);
}

/** Declining is one of the things you can do here, so it is one of the choices. */
const DECLINE = "__decline__";

export function FollowUpDialog({
    open,
    onOpenChange,
    booking,
    merchantId,
    profileHref,
    onSent,
    onDeclined,
    onStale,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    booking: Booking;
    merchantId: string | null;
    /** Where the template pack lives, for when none is approved yet. */
    profileHref: string;
    onSent: (booking: Booking) => void;
    /** The server's booking after a decline lands. */
    onDeclined: (booking: Booking) => void;
    /** The staff group got there first — the list is behind, refetch it. */
    onStale: (message: string) => void;
}) {
    const [followUps, setFollowUps] = useState<FollowUp[] | null>(null);
    const [picked, setPicked] = useState<string | null>(null);
    const [reason, setReason] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) { setPicked(null); setReason(""); setError(null); return; }
        if (followUps !== null || !merchantId) return;
        let live = true;
        void getFlow(merchantId)
            .then((flow) => { if (live) setFollowUps(flow.followUps); })
            .catch(() => { if (live) setFollowUps([]); });
        return () => { live = false; };
    }, [open, followUps, merchantId]);

    // Only what Meta has approved and the send has a key for.
    const options = (followUps ?? []).filter(
        (f) => f.templateStatus === "approved" && KEY_BY_LABEL[f.label] !== undefined,
    );
    const chosen = options.find((f) => f.id === picked) ?? null;
    /**
     * Whether a plain reply would deliver right now. The dialog used to skip
     * this and offer approved templates or nothing, so a merchant whose
     * customer had just messaged was told the only way forward was a Meta
     * approval that takes days — while the composer one panel over would have
     * sent the message immediately.
     */
    const windowOpen = isReplyWindowOpen(booking);
    const declining = picked === DECLINE;
    // Declining only means anything while the request is still open.
    const canDecline = booking.status === "Pending";

    const send = async () => {
        setSending(true);
        setError(null);
        try {
            if (declining) {
                const result = await declineBooking(booking.id, reason.trim() || undefined);
                if (result.ok) { onDeclined(result.booking); onOpenChange(false); return; }
                if (result.kind === "not_pending") { onStale(result.message); onOpenChange(false); return; }
                setError(result.message);
                return;
            }
            if (!chosen) return;
            const result = await sendBookingMessage(booking.id, { template: KEY_BY_LABEL[chosen.label] });
            if (result.ok) { onSent(result.booking); onOpenChange(false); return; }
            setError(result.message);
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ZapIcon className="h-5 w-5 text-blue-600" />
                        Follow up
                    </DialogTitle>
                    <DialogDescription>
                        What happens next for {booking.customer}. A template is the only message WhatsApp
                        delivers outside its 24-hour window, and the AI picks the thread back up once they
                        reply.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <p className="text-[13px] leading-[18px] font-medium text-gray-500">What to do</p>
                    {followUps === null ? (
                        <div className="flex justify-center py-8"><Loader2Icon className="h-5 w-5 animate-spin text-gray-400" /></div>
                    ) : (
                        <div className="space-y-2">
                            {options.map((f) => (
                                <button
                                    key={f.id}
                                    type="button"
                                    onClick={() => setPicked(f.id)}
                                    className={cn(
                                        "flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors",
                                        picked === f.id
                                            ? "border-blue-600 bg-blue-50 dark:bg-blue-950/30"
                                            : "border-gray-200 hover:border-blue-400 dark:border-gray-700",
                                    )}
                                >
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{f.label}</span>
                                    {picked === f.id && (
                                        <span className="text-xs font-medium text-blue-600">Selected</span>
                                    )}
                                </button>
                            ))}

                            {options.length === 0 && (
                                <p className="rounded-xl border border-dashed border-gray-300 px-4 py-5 text-center text-sm text-gray-500">
                                    {windowOpen ? (
                                        <>
                                            {booking.customer} messaged recently, so the 24-hour window is still
                                            open — just reply in the thread behind this dialog. Templates are only
                                            needed once it closes, and none is approved yet:{" "}
                                            <Link to={profileHref} className="font-medium text-blue-600 hover:underline">
                                                submit the pack on Business profile
                                            </Link>.
                                        </>
                                    ) : (
                                        <>
                                            No template is approved yet. Meta has to approve one before it can be sent
                                            outside the 24-hour window —{" "}
                                            <Link to={profileHref} className="font-medium text-blue-600 hover:underline">
                                                submit the pack on Business profile
                                            </Link>.
                                        </>
                                    )}
                                </p>
                            )}

                            {/* Declining belongs with the rest: it is one of the things
                                you can do about this booking, not a separate errand. */}
                            {canDecline && (
                                <button
                                    type="button"
                                    onClick={() => setPicked(DECLINE)}
                                    className={cn(
                                        "flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors",
                                        declining
                                            ? "border-rose-500 bg-rose-50 dark:bg-rose-950/30"
                                            : "border-gray-200 hover:border-rose-400 dark:border-gray-700",
                                    )}
                                >
                                    <span className="flex items-center gap-2 text-sm font-medium text-rose-700 dark:text-rose-400">
                                        <XCircleIcon className="h-4 w-4" />
                                        Decline this booking
                                    </span>
                                    {declining && (
                                        <span className="text-xs font-medium text-rose-600">Selected</span>
                                    )}
                                </button>
                            )}
                        </div>
                    )}

                    {chosen && (
                        <div className="space-y-2">
                            <p className="flex items-center gap-1.5 text-[13px] leading-[18px] font-medium text-gray-500">
                                <MessageCircleIcon className="h-3.5 w-3.5" />
                                What {booking.customer} receives
                            </p>
                            <WaPreview text={fill(chosen.message, booking)} />
                        </div>
                    )}

                    {declining && (
                        <div className="space-y-3">
                            <p className="rounded-xl bg-rose-50 ring-1 ring-inset ring-rose-200 px-3 py-2 text-xs text-rose-800 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-300">
                                {booking.customer} is told the {booking.service} on {(() => { const w = slotWhen(booking); return w ? `${w.date}, ${w.time}` : booking.slot; })()} is not
                                available and asked whether another day or time would work. The request moves
                                to Closed, and nothing here is undone automatically.
                            </p>
                            <div className="space-y-2">
                                <Label htmlFor="rcp-decline-reason" className="text-[13px] leading-[18px] font-medium text-gray-500">
                                    Reason (optional)
                                </Label>
                                <Textarea
                                    id="rcp-decline-reason"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Why the slot cannot be taken — kept for your team, not sent to the customer."
                                    className="min-h-[80px] resize-y"
                                />
                            </div>
                        </div>
                    )}

                    {error && <p className="text-xs text-rose-600">{error}</p>}
                </div>

                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancel</Button>
                    {declining ? (
                        <Button variant="destructive" onClick={() => void send()} disabled={sending}>
                            {sending ? "Declining…" : "Decline booking"}
                        </Button>
                    ) : (
                        <Button onClick={() => void send()} disabled={!chosen || sending}>
                            {sending ? <><Loader2Icon className="mr-1.5 h-4 w-4 animate-spin" /> Sending…</> : <><ZapIcon className="mr-1.5 h-4 w-4" /> Send follow up</>}
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
